import * as THREE from 'three';
import {
  FRAME_SPEED_SCALE,
  FRAME_THICKNESS_SCALE,
  FRAME_Z_STEP,
  type FlowFrames,
} from '../sim/avalanche/flow.ts';
import {
  MOVING_COLOR,
  ORIGIN_ENTRAINED_COLOR,
  ORIGIN_RELEASED_COLOR,
  STOPPED_COLOR,
  pressureColor,
  speedColor,
  thicknessColor,
} from './avalancheColors.ts';
import type { MeshOrigin } from './probe.ts';

/**
 * Renders one avalanche run's recorded frames as a single `THREE.Points` with a custom shader:
 * soft round sprites sized to the particle's physical size (clamped to ~2 px), linearly
 * interpolated between the two frames bracketing the playback time. Particle indices are stable
 * across frames (later frames may have more, from splits) — see `flow.ts`'s `FlowFrame` docs.
 */

export type ParticleColorMode = 'speed' | 'thickness' | 'pressure' | 'origin' | 'moving';

const VERTEX_SHADER = `
  attribute vec3 aColor;
  attribute float aSize;
  attribute float aAlpha;
  varying vec3 vColor;
  varying float vAlpha;
  uniform float uViewportHeight;
  uniform float uFovRad;
  uniform float uPixelRatio;
  void main() {
    vColor = aColor;
    vAlpha = aAlpha;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    float scale = uPixelRatio * (uViewportHeight * 0.5) / tan(uFovRad * 0.5);
    gl_PointSize = max(2.0, aSize * scale / -mvPosition.z);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const FRAGMENT_SHADER = `
  precision mediump float;
  varying vec3 vColor;
  varying float vAlpha;
  void main() {
    vec2 uv = gl_PointCoord - vec2(0.5);
    float d = length(uv);
    if (d > 0.5 || vAlpha <= 0.0) discard;
    float alpha = smoothstep(0.5, 0.15, d) * vAlpha;
    gl_FragColor = vec4(vColor, alpha);
  }
`;

/** Debris/deposit reference density (kg/m³) used to size sprites (PLAN §10). */
export const SPRITE_DENSITY = 250;

function decodeE(x: number, width: number): number {
  return (x / 65535) * width;
}
function decodeS(y: number, height: number): number {
  return (y / 65535) * height;
}
function decodeZ(z: number, zMin: number): number {
  return zMin + z * FRAME_Z_STEP;
}
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export interface AvalancheParticlesOptions {
  frames: FlowFrames;
  time: number;
  colorMode: ParticleColorMode;
  showDeposited: boolean;
  /** The terrain mesh's origin (grid centre) — frame e/s are already relative to the grid's
   * west/north edges, so with the mesh centred at `origin` no further offset is needed; kept for
   * documentation and in case that assumption ever changes. */
  origin: MeshOrigin;
  minElevation: number;
  exaggeration: number;
}

export class AvalancheParticles {
  private readonly scene: THREE.Scene;
  private readonly geometry = new THREE.BufferGeometry();
  private readonly material: THREE.ShaderMaterial;
  private readonly points: THREE.Points;
  private position = new Float32Array(0);
  private color = new Float32Array(0);
  private size = new Float32Array(0);
  private alpha = new Float32Array(0);
  private capacity = 0;
  private disposed = false;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        uViewportHeight: { value: 800 },
        uFovRad: { value: (50 * Math.PI) / 180 },
        uPixelRatio: { value: 1 },
      },
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      transparent: true,
      depthWrite: false,
    });
    this.points = new THREE.Points(this.geometry, this.material);
    this.points.frustumCulled = false;
    this.points.visible = false;
    this.points.renderOrder = 5;
    scene.add(this.points);
  }

  setViewport(heightPx: number, fovDeg: number, pixelRatio: number): void {
    this.material.uniforms.uViewportHeight!.value = Math.max(1, heightPx);
    this.material.uniforms.uFovRad!.value = (fovDeg * Math.PI) / 180;
    this.material.uniforms.uPixelRatio!.value = pixelRatio;
  }

  hide(): void {
    this.points.visible = false;
  }

  private ensureCapacity(n: number): void {
    if (n <= this.capacity) return;
    this.capacity = n;
    this.position = new Float32Array(n * 3);
    this.color = new Float32Array(n * 3);
    this.size = new Float32Array(n);
    this.alpha = new Float32Array(n);
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.position, 3));
    this.geometry.setAttribute('aColor', new THREE.BufferAttribute(this.color, 3));
    this.geometry.setAttribute('aSize', new THREE.BufferAttribute(this.size, 1));
    this.geometry.setAttribute('aAlpha', new THREE.BufferAttribute(this.alpha, 1));
  }

  update(opts: AvalancheParticlesOptions): void {
    if (this.disposed) return;
    const { frames, time, colorMode, showDeposited, origin, minElevation, exaggeration } = opts;
    const list = frames.frames;
    if (list.length === 0) {
      this.hide();
      return;
    }
    let bi = list.length - 1;
    for (let k = 1; k < list.length; k++) {
      if (list[k]!.t >= time) {
        bi = k;
        break;
      }
    }
    const B = list[bi]!;
    const A = list[Math.max(0, bi - 1)]!;
    const span = B.t - A.t;
    const frac = span > 1e-9 ? Math.min(1, Math.max(0, (time - A.t) / span)) : 1;
    const n = B.count;
    if (n === 0) {
      this.hide();
      return;
    }
    this.ensureCapacity(n);
    const halfW = frames.width / 2;
    const halfH = frames.height / 2;
    const pm = frames.particleMass;

    for (let i = 0; i < n; i++) {
      const inA = i < A.count && frac < 1;
      let e: number;
      let s: number;
      let z: number;
      let speed: number;
      let h: number;
      let flags: number;
      if (inA) {
        e = lerp(decodeE(A.x[i]!, frames.width), decodeE(B.x[i]!, frames.width), frac);
        s = lerp(decodeS(A.y[i]!, frames.height), decodeS(B.y[i]!, frames.height), frac);
        z = lerp(decodeZ(A.z[i]!, frames.zMin), decodeZ(B.z[i]!, frames.zMin), frac);
        speed = lerp(A.speed[i]! / FRAME_SPEED_SCALE, B.speed[i]! / FRAME_SPEED_SCALE, frac);
        h = lerp(A.h[i]! / FRAME_THICKNESS_SCALE, B.h[i]! / FRAME_THICKNESS_SCALE, frac);
        flags = B.flags[i]!;
      } else {
        e = decodeE(B.x[i]!, frames.width);
        s = decodeS(B.y[i]!, frames.height);
        z = decodeZ(B.z[i]!, frames.zMin);
        speed = B.speed[i]! / FRAME_SPEED_SCALE;
        h = B.h[i]! / FRAME_THICKNESS_SCALE;
        flags = B.flags[i]!;
      }
      const stopped = (flags & 1) !== 0;
      const gone = (flags & 2) !== 0;
      const entrainedFrac = ((flags >> 2) & 63) / 63;

      this.position[i * 3] = e - halfW;
      this.position[i * 3 + 1] = (z - minElevation) * exaggeration;
      this.position[i * 3 + 2] = s - halfH;
      void origin; // mesh coords share the terrain mesh's origin convention by construction

      let visible = !gone;
      if (stopped && !showDeposited) visible = false;
      this.alpha[i] = visible ? (stopped ? 0.85 : 1) : 0;

      const mass = i < pm.length ? pm[i]! : (pm[pm.length - 1] ?? 1);
      const radius = Math.cbrt((3 * (mass / SPRITE_DENSITY)) / (4 * Math.PI));
      this.size[i] = Math.max(0.15, radius * 2);

      let rgb: readonly [number, number, number];
      switch (colorMode) {
        case 'thickness':
          rgb = thicknessColor(h);
          break;
        case 'pressure': {
          // ρ·v² (kPa) using the sprite reference density; consistent with the deposit overlay.
          const kPa = (SPRITE_DENSITY * speed * speed) / 1000;
          rgb = pressureColor(kPa);
          break;
        }
        case 'origin':
          rgb =
            entrainedFrac <= 0
              ? ORIGIN_RELEASED_COLOR
              : entrainedFrac >= 1
                ? ORIGIN_ENTRAINED_COLOR
                : [
                    ORIGIN_RELEASED_COLOR[0] +
                      (ORIGIN_ENTRAINED_COLOR[0] - ORIGIN_RELEASED_COLOR[0]) * entrainedFrac,
                    ORIGIN_RELEASED_COLOR[1] +
                      (ORIGIN_ENTRAINED_COLOR[1] - ORIGIN_RELEASED_COLOR[1]) * entrainedFrac,
                    ORIGIN_RELEASED_COLOR[2] +
                      (ORIGIN_ENTRAINED_COLOR[2] - ORIGIN_RELEASED_COLOR[2]) * entrainedFrac,
                  ];
          break;
        case 'moving':
          rgb = stopped ? STOPPED_COLOR : MOVING_COLOR;
          break;
        default:
          rgb = speedColor(speed);
          break;
      }
      this.color[i * 3] = rgb[0];
      this.color[i * 3 + 1] = rgb[1];
      this.color[i * 3 + 2] = rgb[2];
    }

    this.geometry.setDrawRange(0, n);
    const posAttr = this.geometry.getAttribute('position') as THREE.BufferAttribute;
    const colorAttr = this.geometry.getAttribute('aColor') as THREE.BufferAttribute;
    const sizeAttr = this.geometry.getAttribute('aSize') as THREE.BufferAttribute;
    const alphaAttr = this.geometry.getAttribute('aAlpha') as THREE.BufferAttribute;
    posAttr.needsUpdate = true;
    colorAttr.needsUpdate = true;
    sizeAttr.needsUpdate = true;
    alphaAttr.needsUpdate = true;
    this.geometry.computeBoundingSphere();
    this.points.visible = true;
  }

  dispose(): void {
    this.disposed = true;
    this.scene.remove(this.points);
    this.geometry.dispose();
    this.material.dispose();
  }
}
